/**
 * Declare an interface TYPE (issue #52).
 *
 * A type is what a program's interfaces are built FROM: one or more of the
 * library's disciplines - Software, Electrical, Mechanical - or the program's own
 * types, combined, plus the attributes this kind of interface always states.
 * `CCSDSInterface :> SoftwareInterface` adding an APID; a
 * `PeripheralCardInterface :> SoftwareInterface, ElectricalInterface` for a card
 * on a standard connector.
 *
 * What the combination already CARRIES is shown as it is chosen, because the
 * attributes somebody is about to declare are only worth declaring if a base
 * does not already have them - and the server refuses a repeat.
 *
 * No package picker: where a program's types live is decided - an
 * `InterfaceTypes` package in its Interfaces package - and created the first time.
 */

import { useEffect, useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import ListSubheader from '@mui/material/ListSubheader'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { AttributeTypeChoice, InterfaceTypeRow } from '../../schema'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import NewAttributesTable from '../parts/NewAttributesTable'
import { attributePayload, emptyAttributeDraft } from '../parts/part-wizard-model'
import type { AttributeDraft } from '../parts/part-wizard-model'
import { DISCIPLINES, typesByDiscipline } from './icd-model'

export default function CreateInterfaceTypeDialog({
  types,
  onClose,
  onCreated,
}: {
  /** Every type there is, library and program, from /api/interface-types. */
  types: InterfaceTypeRow[]
  onClose: () => void
  onCreated: (qualifiedName: string) => void
}) {
  const toasts = useToasts()
  const [name, setName] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [combines, setCombines] = useState<string[]>([])
  const [description, setDescription] = useState('')
  const [attributes, setAttributes] = useState<AttributeDraft[]>([emptyAttributeDraft('attr-0')])
  const [attributeTypes, setAttributeTypes] = useState<AttributeTypeChoice[]>([])
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  useEffect(() => {
    let live = true
    api.attributeTypes()
      .then((answer) => live && setAttributeTypes(answer))
      .catch(() => live && setAttributeTypes([]))
    return () => {
      live = false
    }
  }, [])

  // Only types that reach a discipline can be combined - which is every one a
  // program can make, and the three disciplines themselves.
  const offered = types.filter((type) => type.disciplines.length > 0)
  const grouped = typesByDiscipline(offered)
  const chosen = offered.filter((type) => combines.includes(type.qualified_name))
  const carries = useMemo(
    () => [...new Set(chosen.flatMap((type) => type.carries))],
    [chosen],
  )
  const disciplines = [...new Set(chosen.flatMap((type) => type.disciplines))]

  const submit = async () => {
    setBusy(true)
    setFailure('')
    try {
      const made = await api.createInterfaceType({
        name: name.trim(),
        specializes: combines,
        identifier: identifier.trim() || null,
        description,
        attributes: attributePayload(attributes),
      })
      toasts.succeeded(`${made.name} declared`)
      onCreated(made.qualified_name)
    } catch (error) {
      setFailure((error as ApiError).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>New interface type</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}
          <TextField
            label="Name"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            size="small"
            helperText="How it reads in the model, e.g. CCSDSInterface or PeripheralCardInterface"
            slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
          />

          <FormControl size="small" required>
            <InputLabel id="interface-type-combines">Built on</InputLabel>
            <Select
              labelId="interface-type-combines"
              label="Built on"
              multiple
              value={combines}
              onChange={(event) =>
                setCombines(typeof event.target.value === 'string'
                  ? event.target.value.split(',') : event.target.value)}
              renderValue={(values) => (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {values.map((value) => (
                    <Chip key={value} size="small" label={value.split('::').pop()} />
                  ))}
                </Box>
              )}
            >
              {/* Grouped by discipline, so "a Software one" is one place to look.
                  A type in two disciplines is listed under both; choosing it in
                  either is the same choice. */}
              {DISCIPLINES.flatMap((discipline) => [
                <ListSubheader key={`h-${discipline}`}>{discipline}</ListSubheader>,
                ...grouped[discipline].map((type) => (
                  <MenuItem key={`${discipline}-${type.qualified_name}`} value={type.qualified_name}>
                    {type.name}
                    {type.in_library && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        library
                      </Typography>
                    )}
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>

          {chosen.length > 0 && (
            <Alert severity="info" icon={false}>
              <Typography variant="body2">
                Filed under <strong>{disciplines.join(' and ')}</strong>. Already carries:{' '}
                <Box component="span" sx={{ fontFamily: MONOSPACE }}>
                  {carries.join(', ') || 'nothing yet'}
                </Box>
              </Typography>
            </Alert>
          )}

          <TextField
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            multiline
            minRows={2}
            size="small"
            helperText="What an interface of this type is, for the ICD"
          />

          <Divider />
          <Box>
            <Typography variant="subtitle2">Attributes it adds</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              What every interface of this type states, beyond what it is built on - an APID, a
              bus address. Names are unique across the ICD, so one a base already has is refused.
            </Typography>
            <NewAttributesTable
              rows={attributes}
              choices={attributeTypes}
              onChange={setAttributes}
              disabled={busy}
              showRollsUp={false}
            />
          </Box>

          <TextField
            label="ID"
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            size="small"
            helperText="Optional short name"
            slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={!name.trim() || combines.length === 0 || busy}
        >
          {busy ? 'Declaring…' : 'Declare'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
